import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import { useMutation } from "@apollo/client";
import { useForm, Controller } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { FacilityType, FacilityOperatingStatus } from "@/types";
import { PATCH_FACILITY_MUTATION, FacilityDetailsContactQueryResponse } from "@/features/facility/queries";
import { EditProps } from "./types";
import { NullableStringSchema, URLSchema } from "./validation";

const OfficialSchema = z.object({
  facilityType: NullableStringSchema,
  operatingStatus: NullableStringSchema,
  website: URLSchema
});

type FieldValues = z.infer<typeof OfficialSchema>;

interface OfficialEditFormProps extends EditProps {
  facilityType?: FacilityType;
  operatingStatus?: FacilityOperatingStatus | null;
  website?: string | null;
  facilityTypeOptions: FacilityDetailsContactQueryResponse["facilityTypeOptions"];
  operatingStatusOptions: FacilityDetailsContactQueryResponse["operatingStatusOptions"];
}

const defaultErrorMessage = "Failed to update official details";

export default function OfficialEditForm({
  facilityId,
  facilityType: initialFacilityType,
  operatingStatus: initialOperatingStatus,
  website: initialWebsite,
  facilityTypeOptions,
  operatingStatusOptions,
  onCancel,
  onSuccess,
  onError
}: OfficialEditFormProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      facilityType: initialFacilityType || "",
      operatingStatus: initialOperatingStatus || "",
      website: initialWebsite || ""
    },
    resolver: zodResolver(OfficialSchema),
    mode: "onChange"
  });

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);

  async function onSubmit(values: FieldValues) {
    try {
      const input = {
        id: facilityId,
        ...values
      };

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="official-edit-form">
      <Controller
        name="facilityType"
        control={form.control}
        render={({ field: { onChange, value } }) => (
          <Select
            value={value || ""}
            label="Facility Type"
            onSelect={onChange}
            options={facilityTypeOptions.enumValues.map((o) => ({
              value: o.name,
              label: o.description
            }))}
            disabled={loading}
            error={!!form.formState.errors.facilityType}
            errorText={form.formState.errors.facilityType?.message}
          />
        )}
      />
      <Controller
        name="operatingStatus"
        control={form.control}
        render={({ field: { onChange, value } }) => (
          <Select
            value={value || ""}
            label="Operating Status"
            onSelect={onChange}
            options={[
              ...operatingStatusOptions.enumValues.map((o) => ({
                value: o.name,
                label: o.description
              })),
              { value: "", displayText: "--" }
            ]}
            disabled={loading}
            error={!!form.formState.errors.operatingStatus}
            errorText={form.formState.errors.operatingStatus?.message}
          />
        )}
      />
      <TextField
        className="w-full"
        label="Website"
        value={form.watch("website") || ""}
        {...form.register("website")}
        disabled={loading}
        error={!!form.formState.errors.website}
        errorText={form.formState.errors.website?.message}
        placeholder="https://example.com"
        supportingText="Must start with http:// or https://"
      />
      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
